import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { merge } from 'lodash-es'
import type { DeepPartial } from 'ts-essentials'

import type {
  CascadeActivity,
  RoomActivity,
  RoomActivityId,
  WithId,
} from '@/types'

import type { Options } from './BaseService.types'
import BaseService from './BaseService'

/**
 * @private
 */
export default class RoomActivityService extends BaseService<RoomActivity> {
  constructor(client: AxiosInstance) {
    super(client, 'room-activity')
  }

  get<R extends DeepPartial<RoomActivity> = RoomActivity>(
    id: RoomActivity['id'],
    config?: AxiosRequestConfig & Options
  ): Promise<R> {
    let merged = config ?? {}
    if (config?.relations === true) {
      merged = merge({}, merged, {
        params: {
          join: [
            RoomActivityService.ACTIVITY_RELATION,
            RoomActivityService.ACTIVITY_MEDIA_RELATION,
            RoomActivityService.ACTIVITY_MEDIA_MEDIA_RELATION,
            RoomActivityService.ACTIVITY_STEPS_RELATION,
            RoomActivityService.ACTIVITY_STEPS_MEDIA_RELATION,
            RoomActivityService.ACTIVITY_STEPS_MEDIA_MEDIA_RELATION,
          ],
        },
      })
    }
    return super.get(id, merged)
  }

  async updateActivity(
    roomActivityId: RoomActivityId,
    activity: WithId<CascadeActivity>
  ): Promise<RoomActivity> {
    const { data } = await this.client.patch<RoomActivity>(
      `/room-activity/${roomActivityId}/activity`,
      {
        activity,
      }
    )
    return data
  }

  static ACTIVITY_RELATION = 'activity'
  static ACTIVITY_MEDIA_RELATION = 'activity.media'
  static ACTIVITY_MEDIA_MEDIA_RELATION = 'activity.media.media'
  static ACTIVITY_STEPS_RELATION = 'activity.steps'
  static ACTIVITY_STEPS_MEDIA_RELATION = 'activity.steps.media'
  static ACTIVITY_STEPS_MEDIA_MEDIA_RELATION = 'activity.steps.media.media'
}
